import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleButton, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useAuthUser } from '../hooks/useAuthUser';

const STATO_CFG = {
  ricevuta:       { cls: 'bg-blue-100 text-blue-700' },
  in_valutazione: { cls: 'bg-yellow-100 text-yellow-700' },
  ammessa:        { cls: 'bg-green-100 text-green-700' },
  respinta:       { cls: 'bg-red-100 text-red-700' },
  ritirata:       { cls: 'bg-gray-100 text-gray-500' },
};

const TIPO_CFG = {
  spontanea:       { cls: 'bg-purple-100 text-purple-700' },
  risposta_avviso: { cls: 'bg-indigo-100 text-indigo-700' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

export default function ProposteTab({ procedimentoId }) {
  const navigate = useNavigate();
  const { _locate } = useStateContext();
  const api = usePppApi();
  const { can } = useAuthUser();
  const canCrea = can('ppp.proposte.create');
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!procedimentoId) return;
    setLoading(true);
    api.listProposte(procedimentoId, null, {
      onSuccess: (data) => { setProposte(data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  }, [procedimentoId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="p-4 text-sm text-gray-400">{_locate('ppp_caricamento')}</div>;
  }

  if (proposte.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-gray-400 italic">{_locate('ppp_proposte_nessuna')}</p>
        {canCrea && (
          <SimpleButton
            customFunc={() => navigate(`/ppp/proposte`)}
            text={_locate('ppp_proposta_registra')}
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">
          {proposte.length} {proposte.length === 1 ? 'proposta ricevuta' : 'proposte ricevute'}
        </p>
        {canCrea && (
          <SimpleButton
            customFunc={() => navigate(`/ppp/proposte`)}
            text={_locate('ppp_proposta_registra')}
          />
        )}
      </div>

      {proposte.map((p) => {
        const statoCfg  = STATO_CFG[p.stato]         ?? { cls: '' };
        const tipoCfg   = TIPO_CFG[p.tipoProposta]   ?? { cls: '' };

        return (
          <div
            key={p.id}
            className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900 truncate">{p.titolo}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${statoCfg.cls}`}>
                  {_locate(`ppp_proposta_stato_${p.stato}`) ?? p.stato}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${tipoCfg.cls}`}>
                  {_locate(`ppp_proposta_tipo_${p.tipoProposta}`) ?? p.tipoProposta}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {p.numeroProtocollo && (
                  <span className="font-mono">{p.numeroProtocollo}</span>
                )}
                {p.operatoreRagioneSociale && (
                  <span>{p.operatoreRagioneSociale}</span>
                )}
                {p.dataRicezione && (
                  <span>{formatDate(p.dataRicezione)}</span>
                )}
              </div>
            </div>
            <SimpleButton
              customFunc={() => navigate(`/ppp/proposte/${p.id}`)}
              text={_locate('ppp_apri')}
            />
          </div>
        );
      })}
    </div>
  );
}
